import { InlineMarkdown } from "@/components/InlineMarkdown";
import { Button } from "@/components/ui/button";
import { api, type ConnectorDetail, type ToolState } from "@/lib/api";
import { STATE_HINT, VERIFY_ACTION, invalidateConnectors } from "@/lib/connectors";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { RiskBadge, StateToggle } from "./parts";

// On, ask first or off for each action of the running version. Ask first
// pauses Boo until a person approves that exact run.
export function ActionsPanel({ connector, containerId }: { connector: ConnectorDetail; containerId: string }) {
  const queryClient = useQueryClient();
  const canEdit = connector._permissions.canConfigure;
  const saved = Object.fromEntries(connector.actions.map((a) => [a.name, a.state]));
  const [draft, setDraft] = useState<Record<string, ToolState>>(saved);
  const changes = Object.fromEntries(Object.entries(draft).filter(([name, st]) => saved[name] !== undefined && saved[name] !== st));
  const dirty = Object.keys(changes).length > 0;

  const save = useMutation({
    mutationFn: () => api.connectors.setActionStates(containerId, connector.slug, changes),
    onSuccess: ({ actions }) => {
      setDraft(Object.fromEntries(actions.map((a) => [a.name, a.state])));
      toast.success("Actions updated");
      invalidateConnectors(queryClient, containerId);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not save"),
  });

  if (connector.actions.length === 0) {
    return <p className="rounded-lg border p-6 text-center text-sm text-muted-foreground">This custom integration has no actions yet.</p>;
  }

  const actions = [...connector.actions].sort(
    (a, b) => Number(b.name === VERIFY_ACTION) - Number(a.name === VERIFY_ACTION),
  );

  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-muted-foreground text-pretty">
        {connector.running
          ? "Choose what Boo may run. New actions in a later version start off."
          : "These apply once the first version runs: actions that read start on, actions that change data ask first, and actions that delete start off."}
      </p>
      <div className="divide-y rounded-lg border">
        {actions.map((a) => {
          const state = draft[a.name] ?? a.state;
          return (
            <div key={a.name} className="flex flex-col gap-2 p-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <code className="font-mono text-sm font-medium">{a.name}</code>
                  <RiskBadge risk={a.risk} />
                </div>
                <InlineMarkdown text={a.description} className="mt-0.5 block text-xs text-muted-foreground text-pretty" />
                <p className="mt-1 text-xs text-muted-foreground">
                  {a.name === VERIFY_ACTION
                    ? "Always runs when the connection is saved or tested. This setting is for Boo checking the connection itself."
                    : STATE_HINT[state]}
                </p>
              </div>
              <StateToggle
                label={`${a.name} access`}
                value={state}
                disabled={!canEdit || !connector.running}
                onChange={(next) => setDraft((d) => ({ ...d, [a.name]: next }))}
              />
            </div>
          );
        })}
      </div>
      {canEdit && connector.running && (
        <div className="sticky bottom-0 flex items-center justify-end gap-3 border-t bg-background/95 py-3 backdrop-blur supports-backdrop-filter:bg-background/80">
          {dirty && (
            <Button size="sm" variant="ghost" onClick={() => setDraft(saved)}>
              Discard
            </Button>
          )}
          <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      )}
    </div>
  );
}
